import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
};

/**
 * Small accessible dialog. Closes on Escape or backdrop click, moves focus to
 * the close button on open, and returns focus to the trigger on close.
 */
export function Modal({
  open,
  onClose,
  title,
  children
}: ModalProps) {
  const reduceMotion = useReducedMotion();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    triggerRef.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      triggerRef.current?.focus();
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-348" data-mp="2n2sesac0v3h1">
      {open ? <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15,
      ease: 'easeOut'
    }} data-id="element-349" data-mp="2n1r261fcy8x7">
          <button type="button" aria-label="Close dialog" onClick={onClose} className="absolute inset-0 h-full w-full cursor-default bg-brand-navy/45" data-id="element-350" data-mp="1zdhmlh8qmhei" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="modal-title" className="relative w-full max-w-sm rounded-[24px] border-2 border-brand-navy bg-white p-7 text-center shadow-[0_18px_40px_-24px_rgba(11,72,154,0.55)]" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={reduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.98,
        y: 8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-351" data-mp="1pfjxysmyuild">
            <button ref={closeRef} type="button" onClick={onClose} className="absolute right-4 top-4 rounded-full border-2 border-transparent p-1.5 text-brand-navy transition-colors duration-150 hover:border-brand-navy" data-id="element-352" data-mp="3ddkqs9an4e7l">
              <XIcon aria-hidden="true" className="h-4 w-4" data-id="element-353" data-mp="4y10nj59nbwr" />
              <span className="sr-only" data-id="element-354" data-mp="3w3aiuon0qlvt">Close</span>
            </button>
            <h2 id="modal-title" className="mt-2 font-display text-2xl font-extrabold leading-snug text-brand-navy" data-id="element-355" data-mp="3w1hejfzvodqz">
              {title}
            </h2>
            <div className="mt-3 font-sans text-[15px] leading-relaxed text-ink" data-id="element-356" data-mp="2kpgjxkgc1v3b">{children}</div>
          </motion.div>
        </motion.div> : null}
    </AnimatePresence>;
}